import classnames from "classnames";
import i18next from "i18next";
import { inject } from "mobx-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Translation } from "react-i18next";
import type { Store } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { getReCaptchaLib, Grecaptcha } from "../../lib/recaptcha";
import { SubmitHandler, useForm } from "react-hook-form";
import { Spinner } from "../../ui/Spinner.component";
import "./ContactForm.style.scss";
import { sendEmail } from "../../services/PostUp";
import { PostupMailingStatus } from "@ihr-radioedit/inferno-webapi";

const log = ILog.logger("ContactForm.component");

interface ContactFormProps {
  store?: Store;
}

type ContactFormValues = {
  name: string;
  sendto: string;
  email: string;
  message: string;
  reply: boolean;
};

const base_uri = "/api/v4";

export const ContactForm = inject("store")(({ store }: ContactFormProps) => {
  if (!store) {
    return null;
  }

  const site = store?.site;
  const widgetRef = useRef<string | undefined>(undefined);
  const recaptchaRef = useRef<Grecaptcha | null>(null);

  if (!site || !site.sections.contact?.email_form_switch) {
    return null;
  }

  const loadCaptcha = useCallback(async () => {
    try {
      if (!store.env.GOOGLE_RECAPTCHA_V3_PUBLIC_KEY) {
        return false;
      }
      const design = site.config.sections?.design;

      recaptchaRef.current = await getReCaptchaLib();

      recaptchaRef.current.ready(async () => {
        log.debug("reCAPTCHA loaded");
        widgetRef.current = recaptchaRef.current?.render("recaptcha-badge", {
          sitekey: store.env.GOOGLE_RECAPTCHA_V3_PUBLIC_KEY ?? "",
          badge: "inline",
          size: "invisible",
          theme: design?.ihr_embed_player_bar_stream_theme ?? "dark",
        });
      });
    } catch (e) {
      log.error(e);
    }
  }, [store.env.GOOGLE_RECAPTCHA_V3_PUBLIC_KEY, site]);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [sendStatus, setSendStatus] = useState<PostupMailingStatus | undefined>();
  const [statusMsg, setStatusMsg] = useState("");

  const { register, handleSubmit, reset, formState } = useForm<ContactFormValues>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      sendto: "",
      email: "",
      name: "",
      message: "",
      reply: false,
    },
  });

  const { errors } = formState;
  const sendForm = useCallback(
    async (inputs: ContactFormValues, recaptchaResponse: string) => {
      store.onAnalyticsAction.dispatch({
        sectionName: "email_us",
        pageName: store.page.currentPage ? `${store?.microsite ? "microsite_" : ""}${store.page.currentPage.name}` : "",
        context: "submit",
        action: "click",
      });

      const emailVariables = {
        slug: store.site.index.slug,
        input: {
          senderIP: "n/a", // set on server for now
          senderEmail: inputs.email,
          senderName: inputs.name,
          message: inputs.message,
          subject: "",
          recipientType: inputs.sendto,
          replyDesired: inputs.reply,
        },
      };

      if (recaptchaResponse !== "") {
        const data = await sendEmail({ variables: emailVariables, base_uri, recaptchaResponse });
        setSendStatus(data?.status);
        setStatusMsg(
          data?.status === PostupMailingStatus.Done ? i18next.t("contact_success") : i18next.t("contact_error"),
        );
      }
    },
    [store],
  );

  const onSubmit: SubmitHandler<ContactFormValues> = useCallback(
    async (inputs, event) => {
      event?.preventDefault();
      if (!formState.isValid || isProcessing || !recaptchaRef.current) {
        return false;
      }

      setIsProcessing(true);

      recaptchaRef.current.ready(async () => {
        const response =
          (await recaptchaRef.current?.execute(widgetRef.current ?? "", {
            action: "email_us",
          })) ?? "";

        try {
          await sendForm(inputs, response).then(() => {
            reset();
            setIsProcessing(false);
          });
        } catch (error) {
          log.error(error);
        }
      });
    },
    [sendForm, reset, isProcessing, setIsProcessing, formState, recaptchaRef],
  );

  const formStatusClass = classnames("form-status", {
    [`status-${sendStatus}`]: sendStatus,
  });

  return (
    <section title="form" className="contact-form-container">
      <Translation>{t => <h2>{t("emailUs")}</h2>}</Translation>
      <Translation>{t => <p>{t("contact_desc", { station: `${site.sections.general?.name}` })}</p>}</Translation>
      <form className="contact-form" id="contactForm" name="contactForm" onSubmit={handleSubmit(onSubmit)}>
        <section data-name="name">
          <Translation>
            {t => (
              <Fragment>
                <label className="control-label" htmlFor="name">
                  {t("name")}
                </label>
                <input
                  id="name"
                  name="name"
                  className="form-control"
                  type="text"
                  aria-invalid={errors.name ? "true" : "false"}
                  ref={register({
                    required: { value: true, message: t("contact_form_name_required") },
                    minLength: { value: 3, message: t("contact_form_name_minlength") },
                  })}
                  placeholder={t("name")}
                />
                {errors.name && <span className="form-error">{errors.name.message}</span>}
              </Fragment>
            )}
          </Translation>
        </section>
        <section data-name="email">
          <Translation>
            {t => (
              <Fragment>
                <label className="control-label" htmlFor="email">
                  {t("email")}
                </label>
                <input
                  id="email"
                  name="email"
                  className="form-control"
                  type="email"
                  aria-invalid={errors.email ? "true" : "false"}
                  ref={register({
                    required: { value: true, message: t("contact_form_email_required") },
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: t("contact_form_email_pattern"),
                    },
                  })}
                  placeholder={t("email")}
                />
                {errors.email && <span className="form-error">{errors.email.message}</span>}
              </Fragment>
            )}
          </Translation>
        </section>
        <section data-name="recipient" className="full-width ">
          <Translation>
            {t => (
              <label className="control-label" htmlFor="sendto">
                {t("sendto")}
              </label>
            )}
          </Translation>
          <Translation>
            {t => (
              <Fragment>
                <select
                  id="sendto"
                  name="sendto"
                  className="form-control"
                  aria-invalid={errors.sendto ? "true" : "false"}
                  ref={register({
                    required: { value: true, message: t("contact_form_sendto_required") },
                  })}
                  defaultValue=""
                >
                  <option aria-label="Select who you want to contact" value="" disabled={true}>
                    {t("select")}
                  </option>
                  {store?.site?.sections.contact?.contacts
                    ? store.site.sections.contact.contacts.map((contact, i) => (
                        <option key={i} value={contact.type || ""}>
                          {t(contact.type || "")}
                        </option>
                      ))
                    : null}
                </select>
                {errors.sendto && <span className="form-error">{errors.sendto.message}</span>}
              </Fragment>
            )}
          </Translation>
        </section>
        <section data-name="message" className="full-width">
          <Translation>
            {t => (
              <label className="control-label" htmlFor="message">
                {t("message")}
              </label>
            )}
          </Translation>
          <Translation>
            {t => (
              <Fragment>
                <textarea
                  id="message"
                  name="message"
                  className="form-control i18n"
                  aria-invalid={errors.message ? "true" : "false"}
                  ref={register({
                    required: { value: true, message: t("contact_form_message_required") },
                    minLength: { value: 10, message: t("contact_form_message_minlength") },
                  })}
                  placeholder={t("help")}
                />
                {errors.message && <span className="form-error">{errors.message.message}</span>}
              </Fragment>
            )}
          </Translation>
        </section>
        <section data-name="reply" className="full-width">
          <label htmlFor="reply">
            <input id="reply" name="reply" ref={register} type="checkbox" />
            <Translation>{t => <span>{t("reply")}</span>}</Translation>
          </label>
        </section>
        <section data-name="captcha" className="full-width">
          <Translation>
            {t => (
              <div style={{ display: "none" }} className="form-error" id="error-recaptcha">
                {t("captcha")}
              </div>
            )}
          </Translation>
        </section>
        <section data-name="actions" className="full-width submit-wrapper">
          <Translation>
            {t => (
              <button
                disabled={!!Object.keys(errors).length || isProcessing}
                aria-label="submit contact form"
                className="component-button button-filled"
                type="submit"
              >
                {isProcessing ? <Spinner visible={true} /> : t("submit")}
              </button>
            )}
          </Translation>
        </section>
      </form>
      <div className={formStatusClass}>
        <div className="message">{statusMsg}</div>
      </div>
    </section>
  );
});
